import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' }) : '—';

export default function VerifyCertificate() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, record: null, error: null });

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/verify/${token}`);
        setState({ loading: false, record: res.data?.data || res.data, error: null });
      } catch (err) {
        setState({
          loading: false,
          record: null,
          error: err.response?.data?.message || 'Could not verify certificate',
        });
      }
    })();
  }, [token]);

  const { loading, record, error } = state;

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 24,
    }}>
      <div style={{
        width: '100%',
        maxWidth: 640,
        background: '#fff',
        borderRadius: 16,
        boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          background: record?.valid
            ? 'linear-gradient(135deg,#198754,#157347)'
            : error
              ? 'linear-gradient(135deg,#dc3545,#b02a37)'
              : 'linear-gradient(135deg,#0d6efd,#0a58ca)',
          color: '#fff',
          padding: '36px 32px',
          textAlign: 'center',
        }}>
          {loading ? (
            <>
              <div className="spinner-border text-white mb-3" role="status" />
              <h4 className="mb-0">Verifying…</h4>
            </>
          ) : record?.valid ? (
            <>
              <i className="bi bi-patch-check-fill" style={{ fontSize: 56 }} />
              <h3 className="mt-2 mb-1">Certificate Verified</h3>
              <div style={{ opacity: 0.9, fontSize: 14 }}>
                This certificate is genuine and issued by {record.tenant?.name || 'the institution'}.
              </div>
            </>
          ) : record && !record.valid ? (
            <>
              <i className="bi bi-x-octagon-fill" style={{ fontSize: 56 }} />
              <h3 className="mt-2 mb-1">Certificate Revoked</h3>
              <div style={{ opacity: 0.9, fontSize: 14 }}>This certificate has been revoked and is no longer valid.</div>
            </>
          ) : (
            <>
              <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: 56 }} />
              <h3 className="mt-2 mb-1">Not Found</h3>
              <div style={{ opacity: 0.9, fontSize: 14 }}>{error}</div>
            </>
          )}
        </div>

        {/* Body */}
        <div style={{ padding: 32 }}>
          {loading ? (
            <div className="text-muted">Checking verification token…</div>
          ) : error ? (
            <div className="text-muted small">
              The verification link you followed is invalid or has been tampered with. If you believe this is an error,
              please contact the issuing institution.
            </div>
          ) : record ? (
            <>
              <table className="table table-sm mb-0">
                <tbody>
                  <tr>
                    <td className="text-muted" style={{ width: '40%' }}>Certificate Number</td>
                    <td className="font-monospace">{record.certificateNumber}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Student Name</td>
                    <td><strong>{record.studentName || '—'}</strong></td>
                  </tr>
                  <tr>
                    <td className="text-muted">Course</td>
                    <td>{record.courseName || '—'}</td>
                  </tr>
                  {record.batchNumber && (
                    <tr>
                      <td className="text-muted">Batch</td>
                      <td>{record.batchNumber}</td>
                    </tr>
                  )}
                  <tr>
                    <td className="text-muted">Completion Date</td>
                    <td>{record.completionDate || '—'}</td>
                  </tr>
                  {record.grade && (
                    <tr>
                      <td className="text-muted">Grade</td>
                      <td><strong>{record.grade}</strong></td>
                    </tr>
                  )}
                  <tr>
                    <td className="text-muted">Issued On</td>
                    <td>{fmtDate(record.issuedAt)}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Issued By</td>
                    <td>{record.tenant?.name || '—'}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Status</td>
                    <td>
                      {record.valid
                        ? <span className="badge bg-success">Valid</span>
                        : <span className="badge bg-danger">Revoked</span>}
                    </td>
                  </tr>
                  {!record.valid && record.revokedAt && (
                    <>
                      <tr>
                        <td className="text-muted">Revoked On</td>
                        <td>{fmtDate(record.revokedAt)}</td>
                      </tr>
                      {record.revokedReason && (
                        <tr>
                          <td className="text-muted">Reason</td>
                          <td className="text-danger">{record.revokedReason}</td>
                        </tr>
                      )}
                    </>
                  )}
                </tbody>
              </table>
            </>
          ) : null}
        </div>

        {/* Footer */}
        <div style={{ background: '#f8f9fa', padding: '14px 32px', fontSize: 12, color: '#888', textAlign: 'center', borderTop: '1px solid #e9ecef' }}>
          Powered by TSLMS · Tamper-proof verification via unique token
        </div>
      </div>
    </div>
  );
}
